import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { GSAPModule } from '@df/ui/display/gsap/gsap.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentField } from '@icms/core/content';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { MultiColumnBannerAbstract } from '../multi-column-banner/multi-column-banner.abstract';
import {
    PageComponentMultiColumnBannerData,
    PageComponentMultiColumnBannerItemData,
    additionalFields,
    itemsFields
} from '../multi-column-banner/multi-column-banner.component';

export interface PageComponentMultiColumnBannerV3Data extends PageComponentMultiColumnBannerData {
    items: PageComponentMultiColumnBannerV3ItemData[];
    carousel: boolean;
    carousel_loop: boolean;
    carousel_auto_width: boolean;
    carousel_width: number;
    carousel_nested: boolean;
}

export interface PageComponentMultiColumnBannerV3ItemData extends PageComponentMultiColumnBannerItemData {
    grid_column_span: number;
}

@ICmsComponent<PageComponentMultiColumnBannerV3Component>({
    id: 'multi-column-banner-v3',
    name: 'Multi Column Banner (Variant 3)',
    image: 'assets/images/cms/multi-column-banner-v3.png',
    description: 'Four-column banner',
    tags: ['Images', 'Banner', 'Video'],
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'cta_text',
            defaultSize: 4,
            maxSize: 4,
            fields: [
                ...itemsFields,
                // additional
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'section-additional',
                    title: 'Additional'
                },
                <NICmsContentField.INumberField>{
                    key: 'grid_column_span',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Column Span',
                    min: 1,
                    max: 2,
                    step: 1,
                    default: 1,
                    required: true
                }
            ]
        },
        {
            key: 'carousel',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel',
            description: 'Display items in carousel. Only works on mobile',
            default: false
        },
        {
            key: 'carousel_loop',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel Loop',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV3Data) => data.carousel
        },
        {
            key: 'carousel_auto_width',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Carousel Auto Width',
            description: 'Custom carousel slide width',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV3Data) => data.carousel
        },
        <NICmsContentField.INumberField>{
            key: 'carousel_width',
            type: NICmsContentField.EFieldType.number,
            title: 'Carousel Slide Width',
            min: 100,
            max: 375,
            step: 1,
            default: 255,
            canShow: (data: PageComponentMultiColumnBannerV3Data) => data.carousel && data.carousel_auto_width
        },
        <NICmsContentField.IField>{
            key: 'carousel_nested',
            type: NICmsContentField.EFieldType.checkbox,
            title: 'Carousel Nested',
            description: 'Use only on carousels that use same direction as the parent one',
            default: false,
            canShow: (data: PageComponentMultiColumnBannerV3Data) => data.carousel
        },
        ...additionalFields
    ]
})
@Component({
    selector: 'page-component-multi-column-banner-v3',
    templateUrl: './multi-column-banner-v3.html',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        DfUiCarouselModule,
        GridModule,
        GSAPModule,
        HrefComponent,
        ICmsClientModule,
        LocalVimeoPlayerComponent,
        ShellComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentMultiColumnBannerV3Component extends MultiColumnBannerAbstract {
    override component!: PageComponentMultiColumnBannerV3Data;
}
